import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Warning, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

// ConfirmDialog — replacement for window.confirm() across the app.
//
// Built on Radix Dialog rather than a native <dialog> element. Radix
// manages a stack of dialogs and correctly hands off aria-hidden / focus
// trapping when nested — opening a confirm on top of LocationFormModal
// (or any other Radix-based modal) Just Works. The earlier native-<dialog>
// version had the right shape but couldn't escape the parent dialog's
// aria-hidden umbrella, which left its buttons unresponsive in the
// nested-modal case.
//
// API design:
//   - Mount <ConfirmDialogProvider> once at the app root.
//   - Anywhere inside the tree, call `const confirm = useConfirm()` and
//     then `if (await confirm({ title, body, ... })) { ... }`.
//   - The returned promise resolves to `true` on confirm, `false` on
//     cancel, Esc, backdrop click, or any other dismiss path.

export type ConfirmTone = 'default' | 'destructive';

export interface ConfirmOptions {
  title: ReactNode;
  body?: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  /**
   * Visual treatment of the confirm button. 'destructive' uses a red
   * accent to make accidental confirms harder; 'default' uses the lime
   * primary.
   */
  tone?: ConfirmTone;
  /**
   * Portal container override. Set this to the parent native <dialog>
   * element when calling from inside a `showModal()`'d dialog —
   * otherwise the confirm renders BELOW the parent's top-layer and the
   * user sees nothing happen. Same pattern as SelectContent.container.
   */
  container?: HTMLElement | null;
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      // If a confirm is already pending (rare but possible if a long-
      // running action prompts twice), resolve the previous one as a
      // cancel so we never have two dialogs trying to render at once.
      setPending((prev) => {
        prev?.resolve(false);
        return { ...opts, resolve };
      });
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    setPending((prev) => {
      prev?.resolve(value);
      return null;
    });
  }, []);

  // Radix surfaces close (Esc, backdrop, programmatic) through
  // onOpenChange(false). We treat every non-explicit close as a cancel
  // so the promise never hangs.
  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) settle(false);
    },
    [settle],
  );

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmDialog
        pending={pending}
        onOpenChange={onOpenChange}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error(
      'useConfirm: <ConfirmDialogProvider> is missing from the component tree.',
    );
  }
  return ctx.confirm;
}

interface ConfirmDialogProps {
  pending: PendingConfirm | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({
  pending,
  onOpenChange,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');
  const tone = pending?.tone ?? 'default';
  const confirmLabel = pending?.confirmLabel ?? t('confirmDialog.confirm');
  const cancelLabel = pending?.cancelLabel ?? t('confirmDialog.cancel');

  return (
    <DialogPrimitive.Root open={!!pending} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal container={pending?.container ?? undefined}>
        {/* z-[80] sits above LocationFormModal's z-[60]/z-[61] so a confirm
            triggered from inside that modal lands on top, not behind it. */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-[80] bg-foreground/40" />
        <DialogPrimitive.Content
          // Tone-destructive flow auto-focuses cancel so an Enter on a
          // stray focus can't fire the destructive action.
          onOpenAutoFocus={(e) => {
            if (tone === 'destructive') {
              e.preventDefault();
              requestAnimationFrame(() => {
                document
                  .querySelector<HTMLButtonElement>('[data-confirm-cancel]')
                  ?.focus();
              });
            }
          }}
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-[81] flex max-h-[90vh] w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-card text-foreground shadow-xl"
        >
          {/* items-center keeps the icon, title and close button on one
              vertically-centered row. The leading exclamation icon shows for
              both tones — red for destructive, neutral for default. */}
          <header className="flex items-center gap-3 border-b border-border px-6 py-4">
            <span
              aria-hidden
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-pill ${
                tone === 'destructive'
                  ? 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400'
                  : 'bg-muted text-foreground'
              }`}
            >
              <Warning size={20} weight="fill" />
            </span>
            <DialogPrimitive.Title className="flex-1 font-heading text-lg font-bold leading-tight">
              {pending?.title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0"
                aria-label={t('cancel')}
              >
                <X weight="bold" />
              </Button>
            </DialogPrimitive.Close>
          </header>

          {pending?.body && (
            <div className="flex-1 overflow-y-auto px-6 py-4 text-sm text-foreground/80">
              {typeof pending.body === 'string' ? <p>{pending.body}</p> : pending.body}
            </div>
          )}

          <footer className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              data-confirm-cancel
            >
              {cancelLabel}
            </Button>
            <Button
              type="button"
              variant={tone === 'destructive' ? 'destructive' : 'default'}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </footer>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
